import React from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { Url } from '@wix/da-url';
import { BiData, MessageIconClickBiEvent } from '@wix/da-bi/pkg/events';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import { formatDollars } from '../../../../utils/formatting';
import { normalizeOriginalsCompletedOrders } from '../../../../utils/originalsCompletedOrders';
import { useIsCurrentUserDeviationAuthor } from '../../../../utils/hooks/useIsCurrentUserDeviationAuthor';
import { useEditDeviationAction } from '../../../../ActionBar/actionHooks/useEditDeviationAction';
import RequestOriginalButton from '../../../../originals/RequestOriginalButton';

import s from './AncInfo.scss';

interface Props {
  deviation: PapiDeviation;
  className?: string;
}

const AncInfo: React.FC<Props> = ({ deviation }) => {
  const { t } = useTranslation();

  const isOwner = useIsCurrentUserDeviationAuthor(deviation);
  const handleEditClick = useEditDeviationAction(deviation);

  if (!deviation.extended?.anc) {
    return null;
  }

  const {
    basePrice,
    showInProgressCount,
    inProgressOrdersCount = 0,
  } = deviation.extended.anc;
  const { total: completedOrdersCount } = normalizeOriginalsCompletedOrders(
    deviation.extended.anc
  );

  const formattedPrice = formatDollars(basePrice);

  return (
    <div className={s['root']}>
      <div className={s['header']}>{t('sidebar.ancInfo.header')}</div>
      <div className={s['title']}>{deviation.title}</div>

      <div className={s['info-row']}>
        <div className={s['info-label']}>{t('sidebar.ancInfo.startingPrice')}</div>
        <div className={s['info-value']}>{formattedPrice}</div>
      </div>

      {completedOrdersCount > 0 ? (
        <div className={s['info-row']}>
          <div className={s['info-label']}>{t('sidebar.ancInfo.completedOrders')}</div>
          <div className={s['info-value']}>
            {t('sidebar.ancInfo.orders', {
              count: completedOrdersCount,
            })}
          </div>
        </div>
      ) : null}

      {showInProgressCount && inProgressOrdersCount > 0 ? (
        <div className={s['info-row']}>
          <div className={s['info-label']}>{t('sidebar.ancInfo.inProgressOrders')}</div>
          <div className={s['info-value']}>
            {t('sidebar.ancInfo.orders', {
              count: inProgressOrdersCount,
            })}
          </div>
        </div>
      ) : null}

      {isOwner ? (
        <BrandedButton
          variant="originals"
          size="large"
          className={s['primary-button']}
          onClick={handleEditClick}
        >
          {t('common.edit')}
        </BrandedButton>
      ) : (
        <RequestOriginalButton
          className={s['primary-button']}
          deviation={deviation}
          size="large"
          sectionname="side-rail"
          action="modal"
        />
      )}

      {isOwner ? (
        <BrandedButton
          variant="originals-outline"
          size="large"
          className={s['secondary-button']}
          href={Url.studio({
            section: 'orders-originals',
            deviationId: deviation.deviationId,
          })}
        >
          {t('anc.actions.manageOrders')}
        </BrandedButton>
      ) : (
        <BrandedButton
          variant="originals-outline"
          size="large"
          className={s['secondary-button']}
          href={Url.userMessageLink(deviation.author?.username ?? '')}
          biData={BiData<MessageIconClickBiEvent>({
            evid: 470,
            recipientid: deviation.author?.useridUuid ?? '',
            sectionname: 'anc',
          })}
        >
          {t('anc.actions.sendMessage')}
        </BrandedButton>
      )}
    </div>
  );
};

AncInfo.displayName = 'AncInfo';

export default AncInfo;
